import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Component, DOCUMENT, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { EventDetailsColumnType, EventModel, isBespokeSport } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { EpcotConfig, MediaConfig, ScoreboardBackgroundConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { SportsTheme, ThemeBorder, ThemeElevated, injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { AccordionVariant, SportsThemedAccordionDirective } from '@frontend/sports/common/ui/sports-themed-accordion';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { ComponentProxy, ComponentProxyDirective } from '@frontend/sports/deferred/feature/component-loader';
import { MediaTab } from '@frontend/sports/grid/media/feature/model';
import { IMediaRootState, MediaState, mediaStateSelector } from '@frontend/sports/grid/media/feature/state';
import { AdaptiveLayoutService, ColumnSize } from '@frontend/sports/layout/feature';
import { TrackingService, TrackingVideoSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { ClassAutoRemoveDirective } from '@frontend/sports/web/directives-ui';
import { TabBarEvent } from '@frontend/sports/web/tab-bar-utils';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { DsAccordionModule } from '@frontend/ui/accordion';
import { DsCard } from '@frontend/ui/card';
import { DsDivider } from '@frontend/ui/divider';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { round } from 'lodash-es';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { Observable, defer, map } from 'rxjs';

import { AnimationComponentLoaderService, EventAnimationBinding } from '../animation/animation-component-loader.service';
import { ExpansionPanelComponent } from '../common/expansion-panel/expansion-panel.component';
import { MatchSimulationComponent } from '../match-simulation/match-simulation.component';
import { VideoStreamButtonComponent } from '../video-stream/video-stream-button/video-stream-button.component';
import { EventVideoStreamBinding, VideoStreamComponentLoaderService } from '../video-stream/video-stream-component-loader.service';
import { Actions } from './actions';
import { toggleAnimations } from './animations';
import { EmptyMediaComponent } from './empty-media.component';
import { EventSelectorComponent } from './event-selector.component';
import { MediaComponentMapService, MediaComponentState } from './media-component-map.service';
import { MediaTabBarComponent } from './media-tab-bar.component';
import { MediaTrackingService } from './media-tracking.service';
import { UnpinInfoComponent } from './unpin-info.component';
import { VideoPinComponent } from './video-pin.component';

class TrackingEventNames {
    static readonly MediaModule_Animation_Tab_Click = 'MediaModule_Animation_Tab_Click';
    static readonly MediaModule_Video_Tab_Click = 'MediaModule_Video_Tab_Click';
    static readonly MediaModule_Stats_Tab_Click = (sportId: number): string => `Bet_Radar_GridStats_MediaModule_SB_ ${sportId}_Click`;
}

const tabClickTrackingEvent = {
    [EventDetailsColumnType.Video]: TrackingEventNames.MediaModule_Video_Tab_Click,
    [EventDetailsColumnType.Animation]: TrackingEventNames.MediaModule_Animation_Tab_Click,
    [EventDetailsColumnType.Stats]: (sportId: number) => TrackingEventNames.MediaModule_Stats_Tab_Click(sportId),
};

@Component({
    selector: 'ms-media',
    templateUrl: '././media.component.html',
    styleUrl: './media.component.scss',
    providers: [MediaComponentMapService, MediaTrackingService], // additionally registering dedicated tracking
    // service, to make sure modular-tracking.service overrides tracking.service
    animations: [toggleAnimations],
    imports: [
        DynamicIoDirective,
        DynamicComponent,
        AsyncPipe,
        NgClass,
        NgStyle,
        ExpansionPanelComponent,
        MatchSimulationComponent,
        ComponentProxyDirective,
        VideoStreamButtonComponent,
        IconCustomComponent,
        MediaTabBarComponent,
        ClassAutoRemoveDirective,
        EventSelectorComponent,
        VideoPinComponent,
        UnpinInfoComponent,
        EmptyMediaComponent,
        DsCard,
        DsAccordionModule,
        DsDivider,
        SportsThemedCardDirective,
        SportsThemedAccordionDirective,
    ],
    host: {
        class: 'media-host-container',
    },
})
export class MediaComponent extends WidgetComponent<never> implements OnInit, OnDestroy {
    sitecore = inject(Sitecore);
    deviceService = inject(DeviceService);
    private trackingService = inject(TrackingService);
    private mapperService = inject(MediaComponentMapService);
    private mediaConfig = inject(MediaConfig);
    private scrollContainer = inject(ScrollContainerService);
    private routerEventsService = inject(RouterEventsService);
    private mediaService = inject(MediaQueryService);
    private competitionLogosService = inject(CompetitionLogosService);
    private scoreboardBackgrounds = inject(ScoreboardBackgroundConfig);
    private shortTeamNamesService = inject(ShortTeamNamesService);
    private store = inject<Store<IMediaRootState>>(Store);
    private mediaTrackingService = inject(MediaTrackingService);
    private layoutService = inject(AdaptiveLayoutService);

    readonly EXPAND_BUTTON_FADE_TIME = 500; // 0.5 sec

    private mediaState$: Observable<MediaState>;
    private readonly trackingMap = {
        [EventDetailsColumnType.Video]: 'VideoTab',
        [EventDetailsColumnType.Stats]: 'StatsTab',
        [EventDetailsColumnType.Animation]: 'AnimationTab',
    };

    private mediaSize = new Map<string, { target?: string; width: number }[]>([
        [ColumnSize.ExtraLarge, [{ width: 550 }]],
        [ColumnSize.Large, [{ width: 440 }]],
        [ColumnSize.Default, [{ target: 'gt-mw', width: 375 }, { width: 350 }]],
    ]);

    private readonly _doc = inject(DOCUMENT);
    private readonly epcotConfig = inject(EpcotConfig);

    eventStatisticsComponent$ = defer(() => import('../statistics/event-statistics.component')).pipe(map((c) => c.EventStatisticsComponent));

    eventStatisticsComponentOutputs = {
        toggle: (expand: unknown) => this.onFootballStatExpand(!!expand),
    };

    mediaState = signal<MediaComponentState | undefined>(undefined);
    isEpcotEnabled = false;

    tabType = EventDetailsColumnType;
    statsCollapsed = true;
    sportType = SportConstant;
    isExpandedMediaEnabled: boolean;
    isMobileExpand: boolean;
    trackingSource = '';

    isStatsExpanded = false;
    mediaHeight = 0;
    animationComponent: ComponentProxy<EventAnimationBinding>;
    videoStreamComponent: ComponentProxy<EventVideoStreamBinding>;
    streamingMessageHeight?: string;
    isOnEventDetails: boolean;
    private readonly currentTheme = injectCurrentTheme();
    readonly isCustomHover: boolean = ['mgm-dice'].includes(this.currentTheme as SportsTheme);
    readonly noHoverToggleBtn = ['sportingbet-br', 'betboo-br'].includes(this.currentTheme as SportsTheme);
    readonly themeCardElevated: ThemeElevated = { 'sportingbet-br': { isElevated: false }, 'betboo-br': { isElevated: false } };
    readonly themeBorder: ThemeBorder = {
        'sportingbet-br': { hasBorder: true },
        'betboo-br': { hasBorder: true },
    };

    readonly themedAccordionVarient: AccordionVariant = {
        'mgm-dice': 'surface-lowest',
        'sportingbet-br': 'surface-lowest',
        'betboo-br': 'surface-lowest',
        'sports-interaction': 'surface-highest',
    };

    constructor() {
        const loader = inject(AnimationComponentLoaderService);
        const videoLoader = inject(VideoStreamComponentLoaderService);

        super();
        const store = this.store;

        this.mediaState$ = store.select(mediaStateSelector);
        this.animationComponent = loader.getAnimationComponent();
        this.videoStreamComponent = videoLoader.getVideoStreamComponent();
        this.videoStreamComponent.subscribeToOutputs({ toggleMediaExpand: this.toggleMediaExpand });
    }

    private scoreboardBg = toSignal(this.scoreboardBackgrounds.whenReady.pipe(map(() => this.scoreboardBackgrounds.backgrounds)), {
        initialValue: [],
    });
    private sportId = computed(() => this.getSportId(this.mediaState()));
    private isEsport = computed(() => this.mediaState()?.pendingEsport?.sport.isEsport ?? this.mediaState()?.videoEvent?.sport.isEsport ?? false);

    background = computed(() => {
        const isBeSpokeSport = isBespokeSport(this.sportId(), this.isEsport());
        const background = this.scoreboardBg().find((x) => x.sportId === this.sportId() && isBeSpokeSport);

        if (!background) {
            return { videoBg: { background: '' }, statsBg: { 'background-image': '' } };
        }

        const gradient = { 'background-image': `radial-gradient(circle 115px at top, ${background.innerColor}, ${background.outerColor})` };

        return {
            videoBg: this.isEsport() ? gradient : { background: background.outerColor },
            statsBg: gradient,
        };
    });

    ngOnInit(): void {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.init();

        this.mediaState$
            .pipe(
                map((mediaState: MediaState) => this.mapperService.mapContent(mediaState)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((mediaComponentState) => {
                const previous = this.mediaState();

                this.mediaState.set(mediaComponentState);

                const last = mediaComponentState;
                if (last.trackingSource !== TrackingVideoSource.Default) {
                    this.trackingSource = last.trackingSource;
                }

                const scrollToTop =
                    !previous ||
                    last.activeTab !== previous.activeTab ||
                    (last.animationEvent && last.animationEvent.id) !== (previous.animationEvent && previous.animationEvent.id) ||
                    (last.statisticsEvent && last.statisticsEvent.id) !== (previous.statisticsEvent && previous.statisticsEvent.id) ||
                    last.videoEventId !== previous.videoEventId;

                if (scrollToTop) {
                    this.scrollContainer.scrollTo(0, 0);
                }
                this.statsCollapsed = mediaComponentState.isStatsExpanderVisible;

                this.setShortNames();
                this.setAnimation();
                this.setVideoStream();
                this.setStreamMessageHeight();
            });

        this.routerEventsService.currentActivationEnd
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.onCurrentActivationEnd(event));

        this.mediaService
            .observe()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.mediaSizeChanged());

        this.setStreamMessageHeight();
    }

    isAnimationDisabled = computed(() => {
        return Boolean(this.mediaState()?.tabs.find((tab) => tab.id === EventDetailsColumnType.Animation && tab.disabled));
    });

    private setShortNames(): void {
        const event = this.mediaState()?.animationEvent;

        if (!event || !this.competitionLogosService.getLogoUrl(event.sport.id, event.league.id) || !event.participants.length) {
            return;
        }

        if (
            event.participants.length > 0 &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[0], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('01', 0);
        }

        if (
            event.participants.length > 1 &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[1], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('02', 1);
        }
    }

    private setShortName(player: string, index: number): void {
        const scoreboard = this.mediaState()?.animationEvent?.scoreboard as any;
        const participants = this.mediaState()?.animationEvent?.participants;

        if (!scoreboard || !participants) {
            return;
        }

        const val = participants[index].shortName;

        if (scoreboard.playerInfo) {
            scoreboard.playerInfo.players[player].teamName = val;
        }

        if (scoreboard.score?.playerInfo) {
            scoreboard.score.playerInfo.players[player].teamName = val;
        }
    }

    ngOnDestroy(): void {
        this.toggleMediaModule(false);
    }

    tabSelected(event: TabBarEvent<EventDetailsColumnType>, mediaState: MediaComponentState): void {
        const mediaTab: MediaTab = { tabId: event.active.id, trackingSource: TrackingVideoSource.Default };
        const tabIndex = mediaState.tabs.findIndex((tab) => tab.id === mediaTab.tabId);

        if (tabClickTrackingEvent[event.active.id]) {
            mediaTab.trackingSource = TrackingVideoSource.MediaColumn;

            const mapEventObjByTabs: Record<EventDetailsColumnType, EventModel | null> = {
                [EventDetailsColumnType.Stats]: mediaState.statisticsEvent,
                [EventDetailsColumnType.Animation]: mediaState.animationEvent,
                [EventDetailsColumnType.Video]: mediaState.videoEvent,
            };

            this.mediaTrackingService.trackTabClick(event.active.id, mapEventObjByTabs[event.active.id], tabIndex);
        }

        if (!event.active.disabled) {
            this.toggleCollapsedState(mediaState.tabs.length === 1 ? !mediaState.collapsed : false);
            this.updateActiveTab(mediaTab);
        }
        this.setAnimationHeight();
    }

    panelToggled(collapsed: boolean): void {
        this.mediaTrackingService.trackPanelToggle(collapsed);
        this.toggleCollapsedState(!collapsed);
        this.setAnimationHeight();
    }

    getBackground(mediaState: MediaComponentState, type: EventDetailsColumnType): string {
        if (type === EventDetailsColumnType.Animation) {
            const animationHeight = mediaState.isAnimationAvailable ? 'animation-height' : '';

            return animationHeight + ' ' + this.mapperService.getBackgroundcolor(mediaState, type);
        } else if (type === EventDetailsColumnType.Stats) {
            const statsClass = 'stats-column';
            const collapsedClass = !this.isStatsExpanded ? 'prematch-collapsed' : '';

            return statsClass + ' ' + collapsedClass + ' ' + this.mapperService.getBackgroundcolor(mediaState, type);
        }

        return this.mapperService.getBackgroundcolor(mediaState, type);
    }

    onFootballStatExpand(expand: boolean): void {
        this.isStatsExpanded = expand;
    }

    private mediaSizeChanged(): void {
        this.store.dispatch(Actions.breakpointChanged());
    }

    togglePin(isPinned: boolean): void {
        this.mediaTrackingService.trackPin(isPinned);
        this.store.dispatch(Actions.togglePin({ value: !isPinned }));
    }

    toggleMediaExpand = () => {
        this.toggleExpand(!this.mediaState()?.isExpanded);
    };

    toggleExpand(restoreExpandState: boolean): void {
        const state = this.mediaState();
        if (!state) {
            return;
        }
        if (this.deviceService.isMobile) {
            this.isMobileExpand = true;
        }

        const expandedState = state.isExpanded ? 'Collapse' : 'Expand';
        let activeTab = this.trackingMap[state.activeTab];

        if (!state.videoEvent && activeTab === this.trackingMap[EventDetailsColumnType.Video]) {
            activeTab = 'MediaTab';
        }

        this.trackingService.track(trackingConstants.EVENT_MEDIA, {
            [trackingConstants.COMPONENT_SITE_ELEMENT_INTERCATIONS]: `MediaModule_${expandedState}_${activeTab}`,
        });

        this.store.dispatch(Actions.toggleExpand({ value: restoreExpandState }));
    }

    private setAnimationHeight() {
        setTimeout(() => {
            const ele = this._doc.getElementById('mediaContainer');
            this.mediaHeight = ele?.offsetHeight || 0;
        }, 500);
    }

    private setAnimation(): void {
        const event = this.mediaState()?.animationEvent;

        if (event) {
            this.animationComponent.update({ event, scoreboard: event.scoreboard, inColumn: true });
        }
    }

    private init(): void {
        this.store.dispatch(Actions.init());
    }

    private toggleMediaModule(enabled: boolean): void {
        this.store.dispatch(Actions.toggleMediaModule({ value: enabled }));
    }

    private updateActiveTab(mediaTab: MediaTab): void {
        this.store.dispatch(Actions.setActiveTab({ tab: mediaTab }));
    }

    private toggleCollapsedState(isCollapsed: boolean): void {
        this.store.dispatch(Actions.toggleCollapsedState({ value: isCollapsed }));
    }

    private setAvailableExpandLocation(): void {
        this.store.dispatch(Actions.setAvailableExpandLocation({ value: this.isExpandedMediaEnabled }));
    }

    private onCurrentActivationEnd(event: ActivationEnd | undefined): void {
        const snapshot = event?.snapshot;
        const routeTag = snapshot?.data['tag'];
        const sportId = snapshot?.data['model']?.model?.sport?.id ?? 0;
        this.isOnEventDetails = routeTag === RouteTag.EventDetails;

        const isExpandedOnRoute = sportId === this.sportType.Golf && this.isOnEventDetails;

        this.isExpandedMediaEnabled =
            this.mediaConfig.isExpandedMediaEnabled && this.mediaConfig.expandedMediaEnabledLocations.indexOf(routeTag) > -1;

        // Reset/restore column width
        if (
            (this.mediaState()?.isExpanded && !this.isExpandedMediaEnabled) ||
            (this.mediaState()?.restoreExpandState && this.isExpandedMediaEnabled && !this.mediaState()?.isExpanded) ||
            ((this.mediaState()?.isDefaultExpanded || isExpandedOnRoute) && this.isExpandedMediaEnabled && !this.mediaState()?.isExpanded)
        ) {
            this.toggleExpand(true);
        }

        if (this.isExpandedMediaEnabled !== this.mediaState()?.isLocationAllowsExpand) {
            this.setAvailableExpandLocation();
        }

        this.setStreamMessageHeight();
    }

    private getSportId(mediaState: MediaComponentState | undefined): number {
        if (!mediaState) return 0;

        const event = mediaState.pendingEsport ?? mediaState.statisticsEvent ?? mediaState.animationEvent ?? mediaState.videoEvent;
        return event?.sport.id ?? 0;
    }

    private setVideoStream(): void {
        const event = this.mediaState()?.videoEvent;

        if (event) {
            this.videoStreamComponent.update({
                event,
                inColumn: true,
                autoplay: this.mediaState()!.videoAutoplay,
                isExpanded: this.mediaState()!.isExpanded,
                isExpandEnabled: this.isExpandedMediaEnabled,
                trackingSource: this.trackingSource,
            });
        }
    }

    private setStreamMessageHeight(): void {
        if (this.isOnEventDetails && this.mediaState()?.pendingEsport) {
            const sizes = this.mediaSize.get(this.layoutService.current.rightColumnSize) || [];
            for (const size of sizes) {
                if (!size.target || this.mediaService.isActive(size.target)) {
                    this.streamingMessageHeight = `${round((size.width * 9) / 16)}px`;
                    break;
                }
            }
        } else {
            this.streamingMessageHeight = undefined;
        }
    }
}
